import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  AlertTriangle, Bug, Mail, CheckCircle, X, MessageSquare, 
  Shield, FileText, Loader2, Send
} from 'lucide-react';

const BETA_DISCLAIMER_KEY = 'drayly_beta_accepted';
const BETA_DISCLAIMER_VERSION = '1.0'; // Increment to show disclaimer again

export const BetaDisclaimerModal = ({ onAccept }) => {
  const { api, user } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Skip beta modal in test environments or when explicitly disabled
    const isTestEnv = process.env.NODE_ENV === 'test';
    const isDisabled = process.env.REACT_APP_DISABLE_BETA_MODAL === 'true';
    
    if (isTestEnv || isDisabled) {
      localStorage.setItem(BETA_DISCLAIMER_KEY, BETA_DISCLAIMER_VERSION);
      if (onAccept) onAccept();
      return;
    }
    
    // Check localStorage first (fast, avoids flash)
    const storedVersion = localStorage.getItem(BETA_DISCLAIMER_KEY);
    if (storedVersion === BETA_DISCLAIMER_VERSION) {
      return; // Already accepted locally
    }
    
    // iter 264b — read user preference from AuthContext instead of an
    // independent /auth/me fetch with a stolen localStorage token. When
    // AuthProvider hasn't hydrated yet OR the browser is truly logged
    // out, `user` is null and we simply show the modal.
    // iter 307.13 — Cross-device dedupe: if the backend says this user
    // already accepted v1.0 on another device, mirror to localStorage
    // immediately so the modal never flashes on this device again. The
    // existing branch already does this; added an explicit `user === null`
    // short-circuit for the "auth still loading" phase so we don't flash
    // the modal while AuthProvider is hydrating.
    if (user === null) {
      // Auth still hydrating — wait for the next render before deciding.
      return;
    }
    if (user) {
      const backendVersion = user?.preferences?.beta_accepted_version;
      if (backendVersion === BETA_DISCLAIMER_VERSION) {
        // Backend says accepted - sync to localStorage
        localStorage.setItem(BETA_DISCLAIMER_KEY, BETA_DISCLAIMER_VERSION);
      } else {
        setShow(true);
      }
    } else {
      // Defer setState off the synchronous effect path so react-hooks/set-state-in-effect
      // is satisfied. Microtask order: still fires before paint.
      Promise.resolve().then(() => setShow(true));
    }
  }, [user, onAccept]);

  const handleAccept = useCallback(() => {
    if (!accepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    // Save to localStorage
    localStorage.setItem(BETA_DISCLAIMER_KEY, BETA_DISCLAIMER_VERSION);
    setShow(false);
    if (onAccept) onAccept();
    
    // Save to backend (fire-and-forget) via the shared axios instance.
    if (user) {
      api.patch('/auth/preferences', { beta_accepted_version: BETA_DISCLAIMER_VERSION })
        .catch(() => {}); // Silent fail - localStorage is the primary store
    }
  }, [accepted, onAccept, api, user]);

  if (!show) return null;

  return (
    <Dialog open={show} onOpenChange={() => {}}>
      {/* iter 307.12 — Rewritten mobile-safe. Instead of fighting shadcn
          DialogContent's base `grid` + `p-6` + `gap-4` with flex
          overrides (which left the modal unresponsive on phones), the
          entire DialogContent is now itself the scrollable region:
          `max-h-[85dvh]` with `overflow-y-auto`. The footer still sits
          at the bottom of content; if content exceeds viewport, the
          whole modal scrolls as one unit. Simple = works on every
          browser including iOS Safari + older Android WebView. */}
      <DialogContent
        className="bg-slate-900 border-slate-800 max-w-lg w-[95vw] max-h-[85dvh] overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onPointerDownOutside={(e) => e.preventDefault()}
        aria-describedby="beta-notice-description"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-yellow-600/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <DialogTitle className="text-white text-xl">Beta Version Notice</DialogTitle>
              <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/30 mt-1">
                v1.0 Beta
              </Badge>
            </div>
          </div>
          <DialogDescription id="beta-notice-description" className="sr-only">
            One-time disclaimer about beta software. Read the terms and acknowledge to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2" data-testid="beta-disclaimer-body">
          <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
            <p className="text-yellow-200 text-sm leading-relaxed">
              <strong>Welcome to Drayly!</strong> You are using a <strong>Beta version</strong> of our 
              trucking dispatch and fleet management platform. This software is provided &ldquo;as is&rdquo; and 
              may contain bugs, errors, or incomplete features.
            </p>
          </div>
          
          <div className="space-y-3 text-slate-300 text-sm">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-white">No Warranty:</strong> Drayly (a DBA of RDK Services LLC) make no 
                warranties, express or implied, regarding the reliability, accuracy, or availability 
                of this service during the beta period.
              </p>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-white">Limitation of Liability:</strong> We shall not be liable 
                for any direct, indirect, incidental, special, or consequential damages arising from 
                your use of this beta software.
              </p>
            </div>
            
            <div className="flex items-start gap-2">
              <Bug className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-white">Report Issues:</strong> If you encounter any bugs or 
                issues, please report them using our bug report feature or contact us at{' '}
                <a href="mailto:cs@myrdkllc.com" className="text-blue-400 hover:underline">
                  cs@myrdkllc.com
                </a>
              </p>
            </div>
            
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-white">Feedback Welcome:</strong> Your feedback helps us improve. 
                We appreciate your patience as we work to enhance the platform.
              </p>
            </div>
          </div>
          
          <div className="bg-slate-800/50 rounded-lg p-3 mt-4 active:bg-slate-800">
            {/* iter 307.13 — Widened mobile hit target. The whole card is
                a <label> so tapping ANYWHERE inside (including the long
                acknowledgment text) toggles the checkbox. Checkbox
                itself bumped to h-5/w-5 so fat-finger users on phones
                hit it reliably. */}
            <label
              htmlFor="accept-beta"
              className="flex items-start gap-3 cursor-pointer select-none -m-3 p-3"
            >
              <Checkbox
                id="accept-beta"
                checked={accepted}
                onCheckedChange={setAccepted}
                className="mt-0.5 h-5 w-5"
                data-testid="accept-beta-checkbox"
              />
              <span className="text-sm text-slate-300">
                I understand and acknowledge that this is a <strong className="text-white">Beta version</strong>, 
                and I agree to the terms above. I waive any claims against Drayly (a DBA of RDK Services LLC) 
                for issues arising from the use of this beta software.
              </span>
            </label>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <p className="text-xs text-slate-500 mr-auto">
            Questions? Contact <a href="mailto:cs@myrdkllc.com" className="text-blue-400">cs@myrdkllc.com</a>
          </p>
          <Button 
            onClick={handleAccept}
            disabled={!accepted}
            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 w-full sm:w-auto"
            data-testid="accept-beta-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export const BugReportButton = ({ className = '' }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: 'bug',
    subject: '',
    description: '',
    steps: '',
    email: '',
    priority: 'medium'
  });

  const handleSubmit = async () => {
    if (!form.subject || !form.description) {
      toast.error('Please fill in subject and description');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // iter 264b — Submit to the backend bug-reports inbox via the shared
      // axios instance. The legacy mailto fallback still fires on failure.
      const payload = {
        title: form.subject,
        description:
          `Type: ${form.type}\nPriority: ${form.priority}\n` +
          `Contact Email: ${form.email || 'Not provided'}\n\n` +
          `Description:\n${form.description}\n\n` +
          `Steps to Reproduce:\n${form.steps || 'Not provided'}`,
        category: form.type === 'feature' ? 'feature' : (form.type === 'bug' ? 'bug' : 'feedback'),
        severity: form.priority || 'medium',
        route: typeof window !== 'undefined' ? window.location.pathname : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      };
      await api.post('/feedback/bug-reports', payload);
      
      toast.success('Bug report submitted — our team will review it shortly');
      setOpen(false);
      setForm({
        type: 'bug',
        subject: '',
        description: '',
        steps: '',
        email: '',
        priority: 'medium'
      });
    } catch (error) {
      // Fallback to mailto if the API is unreachable so the user can still
      // get the report to us.
      const mailtoLink = `mailto:cs@myrdkllc.com?subject=${encodeURIComponent(`[${form.type.toUpperCase()}] ${form.subject}`)}&body=${encodeURIComponent(
        `Type: ${form.type}\nPriority: ${form.priority}\nContact Email: ${form.email || 'Not provided'}\n\n` +
        `Description:\n${form.description}\n\n` +
        `Steps to Reproduce:\n${form.steps || 'Not provided'}\n\n` +
        `---\nSent from Drayly Beta v1.0\nUser Agent: ${navigator.userAgent}\nTimestamp: ${new Date().toISOString()}`
      )}`;
      window.open(mailtoLink, '_blank');
      toast.error('Could not reach the bug-reports service — opened your email client as a fallback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className={`text-slate-400 hover:text-white hover:bg-slate-800 ${className}`}
        title="Report a Bug"
      >
        <Bug className="h-4 w-4" />
        <span className="ml-2 hidden sm:inline">Report Bug</span>
      </Button>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-400" />
              Report an Issue
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Help us improve Drayly by reporting bugs or suggesting features
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="bug">🐛 Bug Report</SelectItem>
                    <SelectItem value="feature">💡 Feature Request</SelectItem>
                    <SelectItem value="improvement">✨ Improvement</SelectItem>
                    <SelectItem value="question">❓ Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({...form, subject: e.target.value})}
                placeholder="Brief summary of the issue"
                className="bg-slate-950 border-slate-800"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Describe the issue in detail..."
                className="bg-slate-950 border-slate-800 min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Steps to Reproduce (for bugs)</Label>
              <Textarea
                value={form.steps}
                onChange={(e) => setForm({...form, steps: e.target.value})}
                placeholder="1. Go to...\n2. Click on...\n3. See error..."
                className="bg-slate-950 border-slate-800"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Your Email (optional, for follow-up)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="your@email.com"
                className="bg-slate-950 border-slate-800"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2 text-xs text-slate-500 mr-auto">
              <Mail className="h-3 w-3" />
              <span>Reports sent to cs@myrdkllc.com</span>
            </div>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-500"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


// Beta badge component for header
export const BetaBadge = () => (
  <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/30 text-xs">
    BETA
  </Badge>
);
